import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle, AlertCircle } from 'lucide-react';
import './ContactModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function ContactModal({ isOpen, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, rate_limited
  const [errorMessage, setErrorMessage] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setMessage('');
      setStatus('idle');
      setErrorMessage('');
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/api/contact/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, message }),
      });

      const data = await response.json();

      if (response.status === 429) {
        setStatus('rate_limited');
        setErrorMessage('You have reached the daily limit. Please try again tomorrow.');
      } else if (response.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Unable to connect. Please try again later.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cm-overlay" onClick={onClose}>
      <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cm-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="cm-header">
          <span className="cm-badge">CONTACT US</span>
          <h2>Get in Touch</h2>
          <p>Have questions about TouchBase? We'd love to hear from you.</p>
        </div>

        {status === 'success' ? (
          <div className="cm-success">
            <CheckCircle size={48} />
            <h3>Message Sent!</h3>
            <p>Thank you for reaching out. We'll get back to you shortly.</p>
            <button className="cm-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : status === 'rate_limited' ? (
          <div className="cm-rate-limited">
            <AlertCircle size={48} />
            <h3>Daily Limit Reached</h3>
            <p>{errorMessage}</p>
            <button className="cm-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form className="cm-form" onSubmit={handleSubmit}>
            <div className="cm-field">
              <label htmlFor="cm-name">Name</label>
              <input
                id="cm-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                disabled={status === 'loading'}
              />
            </div>

            <div className="cm-field">
              <label htmlFor="cm-email">Email</label>
              <input
                id="cm-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={status === 'loading'}
              />
            </div>

            <div className="cm-field">
              <label htmlFor="cm-message">Message</label>
              <textarea
                id="cm-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help you?"
                rows={4}
                required
                disabled={status === 'loading'}
              />
            </div>

            {status === 'error' && (
              <div className="cm-error">
                <AlertCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}

            <button
              type="submit"
              className="cm-btn-primary"
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <>
                  <span className="cm-spinner" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Message
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ContactModal;
